<!--
	Countermeasures panel for an Identify (curve) feature.
	Uses @safesystem/client-playbook-lib for intervention logic.
-->
<script lang="ts">
	import type { MapGeoJSONFeature } from 'maplibre-gl';
	import {
		calculateCurveFlag,
		ECountermeasureCategory,
		ECurveIdentifyClass,
		EFlag,
		type ETrafficVolume,
		getCurveRecommendations
	} from '@safesystem/client-playbook-lib';
	import SegmentedControl from '#lib/components/SegmentedControl.svelte';
	import Select from '#lib/components/Select.svelte';
	import DataAttributes from './DataAttributes.svelte';
	import { aadtFromLabel } from './aadt';
	import CountermeasureCategories from './CountermeasureCategories.svelte';
	import { getSpeedLimits } from '../filters';
	import IconFlag from '#lib/assets/flag-solid.svg?raw';

	interface Props {
		feature: MapGeoJSONFeature;
	}

	const { feature }: Props = $props();

	const curveClass = $derived(
		ECurveIdentifyClass[feature.properties.curve_context as keyof typeof ECurveIdentifyClass]
	);
	const advisorySpeed = $derived<number>(feature.properties.fhwa_curve_advisory_speed as number);
	const isPaved = $derived(feature.properties.road_seal === 1);

	let injuryCrashes5yr = $state(1);
	const crashOptions = ['0-1', '2', '3', '≥4'].map((label, i) => ({ value: String(i + 1), label }));

	// User can override the data's speed limit and AADT band.
	const speedLimitOptions = $derived(
		getSpeedLimits().map((limit) => ({ value: String(limit), label: String(limit) }))
	);
	let speedLimit = $derived(feature.properties.speed_limit as number);
	let aadtBand = $derived(aadtFromLabel(feature.properties.aadt_band));

	const curveFlag = $derived.by(() => {
		const flag = calculateCurveFlag(speedLimit, advisorySpeed);
		if (flag === EFlag['None']) return undefined;
		return flag === EFlag['Red'] ? 'red' : 'yellow';
	});

	const recommendations = $derived(
		getCurveRecommendations({
			curveClass,
			aadtBand: aadtBand as ETrafficVolume,
			injuryCrashes5yr,
			speedLimit,
			advisorySpeed,
			isPaved
		})
	);

	const sectionsFor = (category: ECountermeasureCategory) => [
		{
			heading: 'Delineation',
			items: recommendations.delineation[category],
			signage:
				category === ECountermeasureCategory.Foundational ? recommendations.signage : undefined
		},
		{ heading: 'Infrastructure', items: recommendations.infrastructure[category] },
		{ heading: 'Surfacing', items: recommendations.surfacing[category] }
	];

	const validCurveClass = $derived(
		Object.keys(ECurveIdentifyClass).includes(feature.properties.curve_context as string)
	);
</script>

{#if !validCurveClass}
	<p class="noTreatment">Treat curves with crash history as required.</p>
{:else}
	<div class="inputs">
		{#if recommendations.dependsOn.crashes}
			<SegmentedControl
				label="Number of Roadway Departure (RwD) crashes in the past 5 years that resulted in fatality or injury:"
				options={crashOptions}
				value={String(injuryCrashes5yr)}
				onchange={(value) => (injuryCrashes5yr = Number(value))} />
		{/if}
		<DataAttributes summary="Modify data attributes for this curve" bind:aadtBand>
			<Select
				label="Speed limit:"
				options={speedLimitOptions}
				value={String(speedLimit)}
				onchange={(value) => (speedLimit = Number(value))} />
		</DataAttributes>
	</div>

	<div class="interventions">
		<div class="interventionsHeader">
			<h3>Interventions</h3>
			{#if curveFlag}
				<span class="flag {curveFlag}">
					<span class="flagIcon" aria-hidden="true">{@html IconFlag}</span>
					{curveFlag === 'red' ? 'Red' : 'Yellow'} flag<span class="screenReaderOnly">
						for use with the Playbook PDF</span>
				</span>
			{/if}
		</div>

		<CountermeasureCategories {sectionsFor} subject="curve" headingLevel={4} />
	</div>
{/if}

<style>
	.noTreatment {
		padding: var(--space-5) var(--space-1);
	}

	.inputs {
		margin-bottom: var(--space-2);
		border-bottom: 2px solid var(--border-strong);
		padding: var(--space-4) var(--space-2) var(--space-4) var(--space-1);
	}

	.interventions {
		padding: var(--space-4) var(--space-1);
	}

	.interventionsHeader {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		margin: var(--space-2) 0 var(--space-4);

		h3 {
			font-size: var(--font-size-lg);
		}
	}

	.flag {
		display: inline-flex;
		align-items: center;
		gap: var(--space-1);
		font-weight: var(--weight-semibold);
		font-size: var(--font-size-sm);

		.flagIcon {
			display: inline-flex;
			width: var(--icon-md);
			height: var(--icon-md);

			:global(svg) {
				width: 100%;
				height: 100%;
			}
		}

		&.red .flagIcon {
			fill: var(--status-danger);
		}

		&.yellow .flagIcon {
			fill: var(--status-warning);
		}
	}
</style>
